// Curved marquee: quotes ride an SVG wave path (textPath), with an angel icon
// dropped into the gap between each quote.
//
// The strip scrolls by advancing `startOffset`, so every glyph is re-placed on
// the curve each frame and the text stays glued to the ribbon. Translating the
// text group sideways is cheaper, but it cannot work on a wave: a horizontal
// shift only maps the curve onto itself at whole multiples of the period, so
// for most of the loop the text drifts above or below the ribbon.
//
// Seamlessness comes from sharing one period between the wave and the text: the
// wave repeats every PERIOD user units, and the text is split into identical
// tiles that each occupy exactly one whole number of periods (forced with a
// letter-spacing nudge). Advancing by one tile therefore lands every tile
// exactly where its neighbour was.
//
// Angels are measured once into arc offsets and then moved by sampling the
// path, so the per-frame cost is geometry on a static path rather than another
// pass of per-character text metrics.
//
// The scroll is desktop-only. Below the breakpoint the strip renders as a single
// static tile — same quotes on the same curve, just not moving.

const SVG_NS = 'http://www.w3.org/2000/svg';
const XLINK_NS = 'http://www.w3.org/1999/xlink';

// Four EN spaces (0.5em each) reserve room for the angel between quotes.
const SPACER = '\u2002\u2002\u2002\u2002';

// Wave geometry — must match `wave_d` in sections/fluevog-quote-wave.liquid.
const BASELINE = 100;
const AMPLITUDE = 62;
const HALF = 540; // width of one half-wave
const PERIOD = HALF * 2;
const START_X = -60; // phase anchor, keeps the left edge of the ribbon put
const VIEW_W = 1440;

// How far the tile length may sit from a whole number of periods before we look
// for a better fit. The remainder is absorbed by letter-spacing.
const FIT_TOLERANCE = 0.03;
const MAX_PERIODS_PER_TILE = 6;
const MAX_UNITS_PER_TILE = 16;

// Arc distance sampled ahead of an angel to work out the tangent it sits on.
const TANGENT_STEP = 2;

// Motion is desktop-only, matching the theme's 750px breakpoint.
const DESKTOP = '(min-width: 750px)';

function wavePath(halfWaves) {
  let d = `M${START_X},${BASELINE}`;
  for (let i = 0; i < halfWaves; i += 1) {
    const x0 = START_X + i * HALF;
    const cy = i % 2 === 0 ? BASELINE - AMPLITUDE : BASELINE + AMPLITUDE;
    d += ` C${x0 + HALF / 3},${cy} ${x0 + (HALF * 2) / 3},${cy} ${x0 + HALF},${BASELINE}`;
  }
  return d;
}

class FvQuoteWave extends HTMLElement {
  connectedCallback() {
    this.wavePathEl = this.querySelector('[data-wave-path]');
    this.scroll = this.querySelector('[data-wave-scroll]');
    if (!this.wavePathEl || !this.scroll || !this.wavePathEl.id) return;

    this.quotes = this.parseQuotes();
    if (!this.quotes.length) return;

    this.angelSrc = this.dataset.angel || '';
    this.reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.desktop = window.matchMedia(DESKTOP);

    // One unit = every quote, each followed by the spacer that holds an angel.
    this.unit = '';
    this.unitGapStarts = [];
    this.quotes.forEach((quote) => {
      this.unit += quote;
      this.unitGapStarts.push(this.unit.length);
      this.unit += SPACER;
    });

    this.build = this.build.bind(this);
    this.tick = this.tick.bind(this);

    // Only fires when the breakpoint is actually crossed, so this can't be
    // triggered by the iOS address bar resizing during a scroll.
    this.desktop.addEventListener('change', this.build);

    // Metrics depend on the script face, so wait for it before measuring.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(this.build);
    } else {
      this.build();
    }
  }

  disconnectedCallback() {
    this.stop();
    this.desktop?.removeEventListener('change', this.build);
  }

  parseQuotes() {
    try {
      const arr = JSON.parse(this.dataset.quotes || '[]');
      return Array.isArray(arr) ? arr.map((q) => String(q).trim()).filter(Boolean) : [];
    } catch (e) {
      return [];
    }
  }

  build() {
    this.stop();
    this.animate = this.desktop.matches && !this.reduced;

    // The path is periodic, so arc length per period can be probed from a short
    // version and stays valid however far we extend it afterwards.
    this.wavePathEl.setAttribute('d', wavePath(4));
    const arcPerPeriod = this.wavePathEl.getTotalLength() / 2;
    if (!arcPerPeriod) return;

    const unitAdvance = this.measureAdvance(this.unit);
    if (!unitAdvance) return;

    const { periods, units } = this.fitTile(arcPerPeriod, unitAdvance);
    this.tileArc = periods * arcPerPeriod;
    const tileWidth = periods * PERIOD;

    // Tiles start at START_X, so cover from there to the right edge. Scrolling
    // needs one spare tile to feed in as the offset advances.
    const covering = Math.max(1, Math.ceil((VIEW_W - START_X) / tileWidth));
    const tileCount = this.animate ? covering + 1 : covering;
    this.wavePathEl.setAttribute('d', wavePath(tileCount * periods * 2));
    this.pathLength = this.wavePathEl.getTotalLength();

    // The speed setting is horizontal px/s. The curve is longer than its own
    // width, so convert to arc units to keep the on-screen pace as authored.
    const speed = parseFloat(this.dataset.speed || '60') || 60;
    this.arcSpeed = speed * (arcPerPeriod / PERIOD);

    const content = this.unit.repeat(units);
    this.renderTiles(content, tileCount, this.tileArc);
    this.fitTiles(this.tileArc, content.length);
    this.buildAngels(units, tileCount);

    this.offset = 0;
    this.applyOffset(0);

    if (this.animate) this.start();
  }

  // Natural advance width of a string in the marquee's own type styles.
  measureAdvance(string) {
    const probe = document.createElementNS(SVG_NS, 'text');
    probe.setAttribute('class', 'fv-quotewave__text');
    probe.style.visibility = 'hidden';
    probe.textContent = string;
    this.scroll.appendChild(probe);
    const advance = probe.getComputedTextLength();
    probe.remove();
    return advance;
  }

  // Pick how many wave periods a tile spans, and how many times the quote set
  // repeats inside it, so the two lengths land as close together as possible.
  fitTile(arcPerPeriod, unitAdvance) {
    let best = { periods: 1, units: 1, error: Infinity };

    for (let periods = 1; periods <= MAX_PERIODS_PER_TILE; periods += 1) {
      const target = periods * arcPerPeriod;
      const units = Math.max(1, Math.round(target / unitAdvance));
      const error = Math.abs(target - units * unitAdvance) / target;

      if (error < best.error) best = { periods, units, error };
      if (error <= FIT_TOLERANCE) return { periods, units };
    }

    if (best.units > MAX_UNITS_PER_TILE) best.units = MAX_UNITS_PER_TILE;
    return best;
  }

  renderTiles(content, tileCount, tileArc) {
    const href = `#${this.wavePathEl.id}`;
    this.scroll.replaceChildren();
    this.tiles = [];
    this.textPaths = [];

    for (let i = 0; i < tileCount; i += 1) {
      const text = document.createElementNS(SVG_NS, 'text');
      text.setAttribute('class', 'fv-quotewave__text');
      text.setAttribute('dominant-baseline', 'middle');

      const textPath = document.createElementNS(SVG_NS, 'textPath');
      textPath.setAttribute('href', href);
      textPath.setAttributeNS(XLINK_NS, 'xlink:href', href);
      textPath.setAttribute('startOffset', (i * tileArc).toFixed(2));
      textPath.textContent = content;

      text.appendChild(textPath);
      this.scroll.appendChild(text);
      this.tiles.push(text);
      this.textPaths.push(textPath);
    }
  }

  // Nudge letter-spacing until a tile measures exactly one tile of arc, so tiles
  // butt up against each other and the loop has no seam.
  fitTiles(tileArc, charCount) {
    if (!this.tiles.length || !charCount) return;

    const sample = this.tiles[0];
    let spacing = 0;

    for (let pass = 0; pass < 3; pass += 1) {
      sample.style.letterSpacing = `${spacing}px`;
      const error = tileArc - sample.getComputedTextLength();
      if (Math.abs(error) < 0.5) break;
      spacing += error / charCount;
    }

    this.tiles.forEach((tile) => {
      tile.style.letterSpacing = `${spacing}px`;
    });
  }

  // One angel per spacer gap. Their distance along the curve is measured once
  // from the laid-out text, after letter-spacing has settled; from then on they
  // ride the path by arc length and never touch text metrics again.
  buildAngels(units, tileCount) {
    this.angels = [];
    if (!this.angelSrc || !this.tiles?.length) return;

    const fontSize = parseFloat(getComputedStyle(this.tiles[0]).fontSize) || 26;
    const height = fontSize * 1.4;
    const width = height * (37 / 30);
    const mid = SPACER.length / 2;
    const sample = this.tiles[0];

    const arcs = [];
    for (let repeat = 0; repeat < units; repeat += 1) {
      const base = repeat * this.unit.length;

      this.unitGapStarts.forEach((gapStart) => {
        try {
          arcs.push(sample.getSubStringLength(0, base + gapStart + mid));
        } catch (e) {
          // Index past the end of this tile — nothing to place.
        }
      });
    }
    if (!arcs.length) return;

    const layer = document.createElementNS(SVG_NS, 'g');

    for (let tile = 0; tile < tileCount; tile += 1) {
      arcs.forEach((arc) => {
        const el = document.createElementNS(SVG_NS, 'image');
        el.setAttributeNS(XLINK_NS, 'xlink:href', this.angelSrc);
        el.setAttribute('href', this.angelSrc);
        el.setAttribute('width', width);
        el.setAttribute('height', height);
        el.setAttribute('preserveAspectRatio', 'xMidYMid meet');
        layer.appendChild(el);
        this.angels.push({ el, arc: tile * this.tileArc + arc });
      });
    }

    this.angelNudge = { x: -width / 2, y: -height / 2 };
    this.scroll.appendChild(layer);
  }

  // Slide every tile and every angel the same distance along the curve. Tiles
  // take a negative startOffset as they run off the left; the glyphs that fall
  // before the start of the path simply aren't drawn.
  applyOffset(offset) {
    this.textPaths?.forEach((textPath, i) => {
      textPath.setAttribute('startOffset', (i * this.tileArc - offset).toFixed(2));
    });

    if (!this.angels?.length) return;
    const { x: nudgeX, y: nudgeY } = this.angelNudge;

    this.angels.forEach(({ el, arc }) => {
      const at = arc - offset;

      if (at < 0 || at > this.pathLength) {
        el.setAttribute('display', 'none');
        return;
      }

      el.removeAttribute('display');
      const point = this.wavePathEl.getPointAtLength(at);
      const ahead = this.wavePathEl.getPointAtLength(Math.min(at + TANGENT_STEP, this.pathLength));
      const angle = (Math.atan2(ahead.y - point.y, ahead.x - point.x) * 180) / Math.PI;

      el.setAttribute(
        'transform',
        `translate(${point.x.toFixed(2)} ${point.y.toFixed(2)}) rotate(${angle.toFixed(2)}) translate(${nudgeX.toFixed(2)} ${nudgeY.toFixed(2)})`
      );
    });
  }

  stop() {
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = null;
    this.last = 0;
    this.observer?.disconnect();
    this.observer = null;
  }

  start() {
    this.paused = false;
    this.last = 0;
    this.raf = requestAnimationFrame(this.tick);

    if (!('IntersectionObserver' in window)) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        this.paused = !entries.some((entry) => entry.isIntersecting);
      },
      { rootMargin: '20% 0px' }
    );
    this.observer.observe(this);
  }

  tick(now) {
    this.raf = requestAnimationFrame(this.tick);

    // Swallow the elapsed time while paused (and on the first frame) so the
    // strip doesn't jump when it comes back into view.
    if (this.paused || !this.last) {
      this.last = now;
      return;
    }

    const dt = Math.min((now - this.last) / 1000, 0.05);
    this.last = now;

    const step = this.dataset.direction === 'reverse' ? -this.arcSpeed : this.arcSpeed;
    this.offset = (this.offset + step * dt) % this.tileArc;
    if (this.offset < 0) this.offset += this.tileArc;

    this.applyOffset(this.offset);
  }
}

if (!customElements.get('fv-quote-wave')) {
  customElements.define('fv-quote-wave', FvQuoteWave);
}
